// src/pages/UserManagementPage.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../apiConfig';
import AuthContext from '../AuthContext';
import './UserManagementPage.css';
import BackToDashboard from "../components/BackToDashboard";

export default function UserManagementPage() {
  const { currentCompany } = useContext(AuthContext);
  const companyId = currentCompany?.id;
  const navigate = useNavigate();

  // --- Roll (bara läs) ---
  const roleLc = (currentCompany?.role || '').toString().trim().toLowerCase();
  const isAdmin = roleLc === 'admin';

  // --- Hooks ---
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState('');

  // form
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('transportör');

  // filter/sök
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALLA');

  // fler bolag
  const [extraCompanies, setExtraCompanies] = useState([]);
  const [extraCompaniesLoading, setExtraCompaniesLoading] = useState(false);

  useEffect(() => {
    if (!currentCompany) return;
    if (!isAdmin) navigate('/dashboard', { replace: true });
  }, [currentCompany, isAdmin, navigate]);

  async function loadExtraCompanies() {
    if (!companyId || !isAdmin) {
      setExtraCompanies([]);
      return;
    }

    setExtraCompaniesLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/company-entities?companyId=${companyId}`,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        }
      );

      const raw = await res.text();
      let data;

      try {
        data = raw ? JSON.parse(raw) : [];
      } catch {
        throw new Error(raw || `HTTP ${res.status}`);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Kunde inte hämta extra bolag');
      }

      const normalized = Array.isArray(data)
        ? data.map((item) => ({
            id: item.id,
            companyName: item.name || '',
            verksamhetsnamn: item.name || '',
            orgNumber: item.org_number || '',
            address: item.address || '',
            gatuadress: item.address || '',
            postalCode: item.postal_code || '',
            postnummer: item.postal_code || '',
            city: item.city || '',
            ort: item.city || '',
            contactPerson: item.contact_person || '',
            kontaktperson: item.contact_person || '',
            contactEmail: item.contact_email || '',
            kontaktEmail: item.contact_email || '',
            contactPhone: item.phone || '',
            kontaktTelefon: item.phone || '',
            isDefault: Boolean(item.is_default),
            createdAt: item.created_at || null,
            updatedAt: item.updated_at || null,
          }))
        : [];

      setExtraCompanies(normalized);
    } catch (e) {
      console.error('Kunde inte läsa extra bolag från backend:', e);
      setExtraCompanies([]);
      setMsg(prev => prev || `Kunde inte hämta extra bolag: ${e.message}`);
    } finally {
      setExtraCompaniesLoading(false);
    }
  }

  async function load() {
    if (!companyId || !isAdmin) return;
    setLoading(true);
    setMsg('');

    try {
      const res = await fetch(`${API_BASE}/users?companyId=${companyId}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(raw);
      }

      if (!res.ok) throw new Error(data?.error || 'Kunde inte hämta användare');
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setMsg(`Fel: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    loadExtraCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isAdmin]);

  useEffect(() => {
    const onFocus = () => {
      loadExtraCompanies();
    };

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isAdmin]);

  async function handleAddUser(e) {
    e.preventDefault();
    setMsg('');

    if (!companyId) {
      return setMsg('Saknar companyId på inloggad användare. Logga ut/in och försök igen.');
    }

    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      return setMsg('Ange en giltig e-postadress.');
    }

    if (!newPassword || newPassword.length < 8) {
      return setMsg('Lösenord måste vara minst 8 tecken.');
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          companyId,
          email: newEmail.trim().toLowerCase(),
          password: newPassword,
          role: newRole,
        }),
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(raw);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Kunde inte skapa användare');
      }

      setUsers(prev => [data.user, ...prev]);
      setNewEmail('');
      setNewPassword('');
      setNewRole('transportör');
      setMsg('Användare tillagd ✅');
    } catch (e) {
      console.error(e);
      if (
        String(e.message).toLowerCase().includes('duplicate') ||
        String(e.message).toLowerCase().includes('unique')
      ) {
        setMsg('E-postadressen finns redan.');
      } else {
        setMsg(`Kunde inte lägga till: ${e.message}`);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveUser(id) {
    if (!window.confirm('Ta bort denna användare?')) return;

    setBusyId(id);
    setMsg('');

    try {
      const res = await fetch(`${API_BASE}/users/${id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(raw);
      }

      if (!res.ok || data?.error) {
        throw new Error(data?.error || 'Kunde inte ta bort användare');
      }

      setUsers(prev => prev.filter(u => u.id !== id));
      setMsg('Användare borttagen ✅');
    } catch (e) {
      console.error(e);
      setMsg(`Kunde inte ta bort: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  }

  function getRoleLabel(role) {
    const r = String(role || '').trim().toLowerCase();

    if (r === 'admin') return 'Admin';
    if (r === 'transportör') return 'Transportör';
    if (r === 'avfallsproducent') return 'Avfallsproducent';
    if (r === 'insamlare') return 'Insamlare';
    if (r === 'handlare & mäklare') return 'Handlare & Mäklare';
    if (r === 'behandlare') return 'Behandlare';

    return 'Användare';
  }

  const filteredUsers = useMemo(() => {
    const qlc = q.trim().toLowerCase();

    return users.filter(u => {
      const matchesQ = !qlc || (u.email || '').toLowerCase().includes(qlc);
      const matchesRole =
        roleFilter === 'ALLA' ||
        (u.role || '').toLowerCase() === roleFilter.toLowerCase();

      return matchesQ && matchesRole;
    });
  }, [users, q, roleFilter]);

  if (!isAdmin) return null;

  return (
    <div className="user-mgmt-container">
      <div style={{ marginBottom: "1.2rem", display: "flex", justifyContent: "center" }}>
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <h2 className="user-mgmt-title">Användarhantering</h2>

      {msg && <div className="um-alert">{msg}</div>}

      {!companyId && (
        <div className="um-alert um-alert-warn">
          Varning: Inloggat företag saknar <code>id</code>. Logga ut/in så att company-id laddas.
        </div>
      )}

      <p className="user-mgmt-instruction">
        Lägg till användare för <b>{currentCompany?.companyName}</b>. De kan sedan logga in med sin e-post och lösenord.
      </p>

      <div className="reports-filters-row" style={{ marginBottom: 12 }}>
        <label>
          Sök:
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="E-post…"
            disabled={loading}
            style={{ marginLeft: 8 }}
          />
        </label>

        <label>
          Roll:
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            disabled={loading}
            style={{ marginLeft: 8 }}
          >
            <option value="ALLA">Alla</option>
            <option value="avfallsproducent">Avfallsproducent</option>
            <option value="transportör">Transportör</option>
            <option value="insamlare">Insamlare</option>
            <option value="handlare & mäklare">Handlare & Mäklare</option>
            <option value="behandlare">Behandlare</option>
            <option value="admin">Admin</option>
          </select>
        </label>
      </div>

      <div className="user-list">
        <h3>Registrerade användare</h3>
        {loading && <p>Laddar…</p>}
        {!loading && filteredUsers.length === 0 && <p>Inga användare hittades.</p>}

        {!loading && filteredUsers.map(u => (
          <div key={u.id} className="user-item">
            <span className="user-email">{u.email}</span>
            <span className="user-role">{getRoleLabel(u.role)}</span>

            {(u.role || '').toLowerCase() !== 'admin' && (
              <button
                className="remove-btn"
                onClick={() => handleRemoveUser(u.id)}
                disabled={busyId === u.id}
                title="Ta bort"
              >
                {busyId === u.id ? 'Tar bort…' : 'Ta bort'}
              </button>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleAddUser} className="add-user-form">
        <h3>Lägg till ny användare</h3>

        <div className="form-group">
          <label htmlFor="newEmail">E-post</label>
          <input
            id="newEmail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="namn@exempel.se"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="newPassword">Lösenord</label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Minst 8 tecken"
            minLength={8}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="newRole">Roll</label>
          <select
            id="newRole"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            disabled={loading}
          >
            <option value="avfallsproducent">Avfallsproducent</option>
            <option value="transportör">Transportör</option>
            <option value="insamlare">Insamlare</option>
            <option value="handlare & mäklare">Handlare & Mäklare</option>
            <option value="behandlare">Behandlare</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <button
          className="btn-primary"
          type="submit"
          disabled={loading || !companyId}
        >
          {loading ? 'Sparar…' : 'Lägg till användare'}
        </button>
      </form>

      <div
        style={{
          marginTop: '2rem',
          padding: '1rem',
          borderRadius: 12,
          background: '#f8faf8',
          border: '1px solid #dfe7e1',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 10 }}>Fler bolag</h3>

        <p style={{ marginTop: 0, color: '#5f6b66' }}>
          Här kan du lägga till och hantera flera verksamhetsutövare kopplade till ditt konto.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/add-company')}
          >
            Lägg till fler bolag
          </button>

          <div
            style={{
              padding: '8px 12px',
              borderRadius: 999,
              background: '#edf7f0',
              color: '#1d3b2a',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Antal bolag: {extraCompanies.length}
          </div>
        </div>

        {extraCompaniesLoading ? (
          <p style={{ margin: 0, color: '#6b7280' }}>Laddar extra bolag…</p>
        ) : extraCompanies.length === 0 ? (
          <p style={{ margin: 0, color: '#6b7280' }}>
            Du har inte lagt till några extra bolag ännu.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {extraCompanies.map((company, index) => (
              <div
                key={company.id || index}
                style={{
                  padding: '12px 14px',
                  background: '#fff',
                  border: '1px solid #e5ece7',
                  borderRadius: 10,
                }}
              >
                <div style={{ fontWeight: 700, color: '#1d3b2a', marginBottom: 4 }}>
                  {company.companyName || company.verksamhetsnamn || `Bolag ${index + 1}`}
                  {company.isDefault ? ' (standard)' : ''}
                </div>

                {company.orgNumber && (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>
                    <b>Org.nr:</b> {company.orgNumber}
                  </div>
                )}

                {company.contactPerson && (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>
                    <b>Kontaktperson:</b> {company.contactPerson}
                  </div>
                )}

                {company.contactEmail && (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>
                    <b>E-post:</b> {company.contactEmail}
                  </div>
                )}

                {company.contactPhone && (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>
                    <b>Telefon:</b> {company.contactPhone}
                  </div>
                )}

                {(company.address || company.postalCode || company.city) && (
                  <div style={{ color: '#6b7280', fontSize: 14 }}>
                    <b>Adress:</b>{' '}
                    {[company.address, company.postalCode, company.city]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}